import { useState } from 'react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { useApprovals } from '@/hooks/use-approvals'
import type { ApprovalStatus } from '@riverpark/types'

const STATUS_TABS: { value: ApprovalStatus | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
]

function statusBadgeVariant(status: ApprovalStatus): 'pending' | 'paid' | 'overdue' {
  if (status === 'pending') return 'pending'
  if (status === 'approved') return 'paid'
  return 'overdue' // rejected or failed
}

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function MyApprovalsPage() {
  const [status, setStatus] = useState<ApprovalStatus | undefined>(undefined)
  const { data, isLoading } = useApprovals(status)
  const rows = data?.data ?? []

  return (
    <PageShell>
      <PageHeader
        title="My requests"
        label="Approvals"
        description="Actions you've submitted that needed committee or admin sign-off before taking effect."
      />

      <div className="flex gap-1 border-b border-border-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setStatus(tab.value)}
            className={
              status === tab.value
                ? 'border-b-2 border-primary px-4 py-2.5 font-sans text-sm font-medium text-ink'
                : 'px-4 py-2.5 font-sans text-sm font-medium text-muted-foreground transition-colors hover:text-ink'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-8 text-center font-sans text-sm text-muted-foreground">
          Nothing here yet — requests you submit for committee/admin approval will show up here.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="rounded border border-border-2 bg-card px-4 py-3">
              <div className="flex items-center gap-2">
                <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>
                <p className="font-sans text-sm font-medium text-ink">{r.summary}</p>
              </div>
              <p className="mt-1 font-sans text-xs text-muted-foreground">
                Requested {formatDateTime(r.createdAt)}
                {r.reviewedByName && r.reviewedAt && (
                  <>
                    {' '}
                    · Decided by {r.reviewedByName} on {formatDateTime(r.reviewedAt)}
                  </>
                )}
              </p>
              <p className="mt-1 font-sans text-xs text-muted-foreground">Reason: {r.reason}</p>
              {r.reviewNote && (
                <p className="mt-1 font-sans text-xs text-muted-foreground">Note: {r.reviewNote}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  )
}
